import React, { CSSProperties } from "react";

interface ServiceCardExportProps {
  title?: string;
  description?: string;
  icon?: string;
  imageUrl?: string;
  link?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  showShadow?: boolean;
  padding?: number;
  textFontFamily?: string;
}

// Matching editor: ServiceCard.tsx — same styles as CMS preview
export const ServiceCardExport: React.FC<ServiceCardExportProps> = ({
  title = "Service Name",
  description = "Brief description of the service we provide to our patients.",
  icon,
  imageUrl,
  link,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  borderRadius = 12,
  showShadow = true,
  padding = 24,
  textFontFamily,
}) => {
  const safePadding = padding || 24;
  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const ffStyle = (text?: string): CSSProperties => textFontFamily ? { fontFamily: `'${textFontFamily}', var(--font-family), sans-serif` } : (text && !hasThai(text)) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {};

  // Matching editor: ServiceCard.tsx line 65-127
  const cardContent = (
    <>
      {imageUrl && (
        <div
          style={{
            marginBottom: "16px",
          }}
        >
          <img
            src={imageUrl}
            alt={title}
            style={{
              width: "100%",
              height: "192px",
              objectFit: "cover" as const,
              borderRadius: `${borderRadius}px ${borderRadius}px 0 0`,
            }}
          />
        </div>
      )}
      {icon && !imageUrl && <div style={{ fontSize: "2.25rem", marginBottom: "16px" }}>{icon}</div>}
      <h3
            style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              marginBottom: "8px",
              color: textColor,
              marginTop: 0,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              ...ffStyle(title),
            }}
          >
            {title}
          </h3>
          <p
            style={{
              color: textColor,
              opacity: 0.8,
              fontSize: "1rem",
              lineHeight: 1.6,
              margin: 0,
              wordWrap: "break-word",
              overflowWrap: "break-word",
              ...ffStyle(description),
            }}
          >
            {description}
          </p>
      {link && (
        <div style={{ marginTop: "16px" }}>
          <span style={{ fontWeight: 500, color: "var(--color-primary, #3b82f6)" }}>Learn More →</span>
        </div>
      )}
    </>
  );

  // Matching editor: ServiceCard.tsx line 130-144
  // Parent carousel overrides flex via --card-width CSS variable
  const cardStyles: CSSProperties = {
    flex: "0 0 var(--card-width, 280px)",
    minWidth: 0,
    backgroundColor,
    borderRadius: `${borderRadius}px`,
    padding: imageUrl ? 0 : `${safePadding}px`,
    paddingBottom: `${safePadding}px`,
    boxShadow: showShadow ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)" : "none",
    overflow: "hidden",
    boxSizing: "border-box",
  };

  const innerPadding = imageUrl ? `${safePadding}px` : 0;

  if (link) {
    return (
      <a href={link} style={{ ...cardStyles, display: "block", textDecoration: "none", color: textColor }} className="service-card">
        <div style={{ padding: `0 ${innerPadding} 0 ${innerPadding}` }}>{cardContent}</div>
      </a>
    );
  }

  return (
    <div style={cardStyles} className="service-card">
      <div style={{ padding: `0 ${innerPadding} 0 ${innerPadding}` }}>{cardContent}</div>
    </div>
  );
};
